/* eslint-disable max-len */
import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react'
import styled from 'styled-components'
import get from 'lodash/get'
import moment from 'moment'
import numeral from 'numeral'
import { StickyTable, Row } from 'react-sticky-table'
import { useDispatch, useSelector } from 'react-redux'
import { AiOutlineFileExcel } from 'react-icons/ai'

import { renderHeader } from './header'
import { modalButtons } from '../../../../buttons/modal-button'
import { Button } from '../../../../buttons'
import { projectInformationAction } from '../../../../../actions'
import { formatNumber } from '../../../../../function/math.js'
import {
  updateLobMonth,
  getMonthList,
  reCalRevenueAllocation,
  // getAllocationDateChange,
} from '../../../../../function/revenue-allocation.js'
import { projectInformationSelector } from '../../../../../selectors'
import {
  ProjectDetail,
  Total,
  ScrollableInput,
} from '../revenue-table/item'
import { FONTS } from '../../../../../constants'

export const RevenueTable = React.memo(forwardRef(({
  hideHeader,
  rSelected,
  toggle,
  projectByID = {},
  lobData = [],
  invalidList,
  handleInvalid,
  selectedLOB,
}, ref) => {
  const dispatch = useDispatch()
  const [crRevenue, setCrRevenue] = useState([])
  const { revenue_allocation = [], presale_snapshot, start_date, end_date } = projectByID
  const snapshot = presale_snapshot ? presale_snapshot.snapshot : null
  const isPresale = presale_snapshot && rSelected === 1
  const [revenueAllocation, setRevenueAllocation] = useState(revenue_allocation)

  const revenueTable = useSelector(projectInformationSelector.getRevenuePlanningData)
  const ppsLatestSubmittedDate = useSelector(projectInformationSelector.getPPSLatestSubmittedDate)
  const incompletedList = useSelector(projectInformationSelector.getIncompletedRevenue)

  const monthList = getMonthList(start_date, end_date)

  useImperativeHandle(ref, () => ({
    isRevenueAllocationEmpty() {
      return !revenueAllocation.length
    },
  }))

  useEffect(() => {
    return () => {
      Promise.all([
        dispatch(projectInformationAction.editRevenuePlanningTable(false)),
        dispatch(projectInformationAction.setRevenuePlanningTable({ expand: {} })),
      ])
    }
  }, [])

  useEffect(() => {
    if (selectedLOB.value !== '') {
      const revenue = revenue_allocation.map(r => {
        return {
          ...r,
          lobs: r.lobs.filter(l => l.lob_id === selectedLOB.value),
        }
      })
      setRevenueAllocation(reCalRevenueAllocation(revenue.filter(r => r.lobs.length), projectByID))
    } else {
      setRevenueAllocation(reCalRevenueAllocation(revenue_allocation, projectByID))
    }
  }, [selectedLOB])

  useEffect(() => {
    if (projectByID && projectByID.revenue_allocation) {
      if (!isPresale) {
        const rev = reCalRevenueAllocation(revenue_allocation, projectByID)
        const invalid = {}
        rev.map((r, rKey) => {
          r.lobs.map((l, lKey) => {
            if (!l.criteria) invalid[`${rKey}_${lKey}_criteria`] = true
            if (!l.lob_id) invalid[`${rKey}_${lKey}_lob_id`] = true
            return l
          })
          return r
        })
        handleInvalid(invalid)
        checkIncompletedRevenue(revenue_allocation)
        setCrRevenue(revenue_allocation.filter(r => r.type === 'CR'))
        setRevenueAllocation(rev)
      } else if (snapshot.revenue_allocation) {
        checkIncompletedRevenue(snapshot.revenue_allocation)
        setCrRevenue(snapshot.revenue_allocation.filter(r => r.type === 'CR'))
        setRevenueAllocation(reCalRevenueAllocation(snapshot.revenue_allocation, snapshot))
      }
    }
  }, [projectByID, rSelected])

  const checkIncompletedRevenue = (revenue) => {
    const list = {}
    revenue.map((r, key) => {
      let sum = 0
      r.lobs.map(l => sum += numeral(l.lob_revenue).value())
      const isNotComplete = r.lobs.some(l => !l.criteria) || r.lobs.some(l => !l.lob_id)
      if (sum !== numeral(r.revenue).value() || isNotComplete) {
        list[key] = true
      }
      return r
    })
    dispatch(projectInformationAction.setIncompletedRevenue(list))
  }

  const handleExpandTable = id => {
    let table = revenueTable.expand
    if (table[id]) {
      delete table[id]
    } else {
      table = { ...table, [id]: true }
    }
    dispatch(projectInformationAction.setRevenuePlanningTable({ expand: table }))
  }

  const handleChangeSelect = (e, parent_id, id, name) => {
    const newAllocations = [...revenueAllocation]
    const lobAllocation = [...newAllocations[parent_id].lobs]
    let allocation = { ...lobAllocation[id] }
    if (allocation[name] !== e.value) {
      allocation = resetLobAllocation(allocation)
    }
    allocation[name] = e.value
    lobAllocation[id] = allocation
    newAllocations[parent_id].lobs = lobAllocation
    if (name === 'criteria' || name === 'lob_id') {
      if (e.value && invalidList[`${parent_id}_${id}_${name}`]) {
        handleInvalid({ ...invalidList, [`${parent_id}_${id}_${name}`]: false })
      }
    }
    checkIncompletedRevenue(newAllocations)
    setRevenueAllocation(reCalRevenueAllocation(newAllocations, projectByID))
  }

  const handleChangeInput = (e, parent_id, id, name) => {
    const newAllocations = [...revenueAllocation]
    const parenAllocation = { ...newAllocations[parent_id] }
    const lobAllocation = [...newAllocations[parent_id].lobs]
    let allocation = { ...lobAllocation[id] }
    const isChanging = allocation[name] !== e.target.value
    allocation[name] = e.target.value
    const criteria = get(allocation, 'criteria', '')
    // const lobRevenue = get(allocation, 'lob_revenue', '')
    if (criteria !== '' && isChanging) {
      const updatedLobMonth = updateLobMonth(allocation, parenAllocation, ppsLatestSubmittedDate)
      allocation = updatedLobMonth
    }
    lobAllocation[id] = allocation
    newAllocations[parent_id].lobs = lobAllocation
    checkIncompletedRevenue(newAllocations)
    setRevenueAllocation(reCalRevenueAllocation(newAllocations, projectByID))
  }

  const handleChangeScollableInput = (e, parent_id, id, sub_id, name) => {
    const newAllocations = [...revenueAllocation]
    const parenAllocation = { ...newAllocations[parent_id] }
    const lobAllocation = [...newAllocations[parent_id].lobs]
    let allocation = { ...lobAllocation[id] }
    const monthAllocation = [...allocation.lob_months]
    monthAllocation[sub_id][name] = e.target.value
    allocation.lob_months = monthAllocation
    const updatedLobMonth = updateLobMonth(allocation, parenAllocation, ppsLatestSubmittedDate, name)
    allocation = updatedLobMonth
    lobAllocation[id] = allocation
    newAllocations[parent_id].lobs = lobAllocation
    checkIncompletedRevenue(newAllocations)
    setRevenueAllocation(reCalRevenueAllocation(newAllocations, projectByID))
  }

  const handleChangeDate = (e, parent_id, id, name) => {
    const newAllocations = [...revenueAllocation]
    const parenAllocation = { ...newAllocations[parent_id] }
    const lobAllocation = [...newAllocations[parent_id].lobs]
    let allocation = { ...lobAllocation[id] }
    if (name === 'start_date') {
      if (moment(e).isAfter(allocation.end_date)) {
        openResponseModal({
          title: 'End date reset',
          description: 'The LOB End date has been reset to the project’s default due to overlapping date.',
        })
        allocation.end_date = projectByID.end_date
      }
    } else if (name === 'end_date') {
      if (moment(e).isBefore(allocation.start_date)) {
        openResponseModal({
          title: 'Start date reset',
          description: 'The LOB start date has been reset to the project’s default due to overlapping date.',
        })
        allocation.start_date = ppsLatestSubmittedDate !== '' ? moment(ppsLatestSubmittedDate).add(1, 'month').startOf('month') : projectByID.start_date
      }
    }
    allocation[name] = e
    const updatedLobMonth = updateLobMonth(allocation, parenAllocation, ppsLatestSubmittedDate)
    allocation = updatedLobMonth
    lobAllocation[id] = allocation
    newAllocations[parent_id].lobs = lobAllocation
    setRevenueAllocation(reCalRevenueAllocation(newAllocations, projectByID))
  }

  const handleChangeRemark = (e, parent_id, id, name) => {
    const newAllocations = [...revenueAllocation]
    const lobAllocation = [...newAllocations[parent_id].lobs]
    const allocation = { ...lobAllocation[id] }
    allocation[name] = e
    lobAllocation[id] = allocation
    newAllocations[parent_id].lobs = lobAllocation
    setRevenueAllocation(reCalRevenueAllocation(newAllocations, projectByID))
  }

  const resetLobAllocation = (allocation) => {
    allocation.lob_revenue = formatNumber(0)
    allocation.lob_percent = formatNumber(0)
    const lobMonths = [...allocation['lob_months']]
    lobMonths.map(lm => {
      lm.ratio = formatNumber(0)
      lm.revenue = formatNumber(0)
      return lm
    })
    allocation.lob_months = lobMonths
    return allocation
  }

  const openResponseModal = text => {
    const actions = { close: closeResponseModal }
    dispatch(projectInformationAction.openResponseModal({
      responseModal: {
        isOpen: true,
        res: {
          title: text.title,
          description: text.description,
          actions: modalButtons('error', actions),
        },
      },
    }))
  }

  const closeResponseModal = () => {
    dispatch(projectInformationAction.openResponseModal({
      responseModal: { isOpen: false, res: {} },
    }))
  }

  return (
    <Root hide_header={ hideHeader }>
      <Sticky
        stickyHeaderCount={ !revenueAllocation.length ? 0 : 1 }
        stickyColumnCount={ 1 }
      >
        { renderHeader(monthList, revenueAllocation, selectedLOB, projectByID) }
        { revenueAllocation.map((v, k) => {
          const isExpand = revenueTable.expand[k]

          return (
            <Row key={ k }>
              <Wrapper>
                <ProjectDetail
                  data={ v }
                  allocation_id={ k }
                  invalidList={ invalidList }
                  lobData={ lobData }
                  crRevenue={ crRevenue }
                  projectByID={ projectByID }
                  incompletedList={ incompletedList }
                  isExpand={ isExpand }
                  handleExpandTable={ handleExpandTable }
                  handleChangeDate={ handleChangeDate }
                  handleChangeSelect={ handleChangeSelect }
                  handleChangeInput={ handleChangeInput }
                  handleChangeRemark={ handleChangeRemark }
                />
                <Total
                  data={ v }
                  allocation_id={ k }
                  isExpand={ isExpand }
                  handleExpandTable={ handleExpandTable }
                />
              </Wrapper>
              <ScrollableInput
                data={ v }
                allocation_id={ k }
                isExpand={ isExpand }
                handleExpandTable={ handleExpandTable }
                handleChangeScollableInput={ handleChangeScollableInput }
              />
            </Row>
          )
        })}
      </Sticky>
      { !revenueAllocation.length && (
        <MessageWrapper>
          <AiOutlineFileExcel style={ { fontSize: 48, color: '#DADADA' } } />
          <Message style={ { marginTop: 24 } }>There are currently no revenue data yet</Message>
          <Message style={ { marginBottom: 24 } }>Add ‘Cost Planning’ to display data for Revenue Planning</Message>
          <Button
            theme='#28a745'
            width='165px'
            height='26px'
            onClick={ () => toggle('2') }
          >
            Go to Cost Planning
          </Button>
        </MessageWrapper>
      ) }
    </Root>
  )
}))

const Root = styled.div`
  width: 100%;
  height: calc(100vh - ${p => (p.hide_header ? 120 : 220)}px);
  display: flex;
  flex-direction: column;
  overflow-y: hidden;
  position: relative;
`

const Wrapper = styled.div`
  width: 540px !important;
  height: 100% !important;
  display: flex;
`

const Sticky = styled(StickyTable)`
  padding-top: 0px;
  padding-right: 0px;
  padding-left: 0px;
  padding-bottom: 11px;

  .y-scrollbar {
    height: 100%;
    top: 0;
    ::-webkit-scrollbar {
      -webkit-appearance: none;
    }
    ::-webkit-scrollbar:vertical {
      width: 11px;
    }
    ::-webkit-scrollbar:horizontal {
      height: 11px;
    }
    ::-webkit-scrollbar-thumb {
      border-radius: 8px;
      border: 2px solid white; /* should match background, can't be transparent */
      background-color: rgba(0, 0, 0, .5);
    }
  }
  .x-scrollbar {
    ::-webkit-scrollbar {
      -webkit-appearance: none;
    }
    ::-webkit-scrollbar:vertical {
      width: 11px;
    }
    ::-webkit-scrollbar:horizontal {
      height: 11px;
    }
    ::-webkit-scrollbar-thumb {
      border-radius: 8px;
      border: 2px solid white; /* should match background, can't be transparent */
      background-color: rgba(0, 0, 0, .5);
    }
  }
`

const MessageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: absolute;
  top: 30%;
  left: 50%;
  transform: translateX(-50%);
`

const Message = styled.span`
  font-family: ${FONTS.GOTHAM};
  font-size: ${FONTS.NORMAL_SIZE};
  color: #B3B3B3;
`



// WEBPACK FOOTER //
// ./src/components/project-information/tab-content/tab-revenue-planning/revenue-table/index.js